import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Menu, Moon, Sun, X } from "lucide-react";
import { navLinks } from "../data/content";
import BrandMark from "./BrandMark";
import { useTheme } from "./ThemeContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const reduceMotion = useReducedMotion();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  return (
    <header
      className={`site-header fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "site-header-scrolled" : ""
      }`}
      data-testid="main-navbar"
    >
      <nav className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <Link to="/" className="brand-link group flex items-center gap-3" data-testid="nav-logo">
          <span className="brand-mark relative grid h-10 w-10 place-items-center rounded-xl transition-transform duration-300 group-hover:rotate-[-8deg]">
            <BrandMark className="h-full w-full" />
          </span>
          <span className="leading-none">
            <span className="brand-name block font-display text-base font-bold tracking-tight">
              Spark Link
            </span>
            <span className="brand-subtitle mt-1 block font-mono text-[9px] uppercase tracking-[0.32em]">
              IT Solutions
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `nav-link relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  isActive ? "nav-link-active" : ""
                }`
              }
            >
              {() => (
                <>
                  {link.label}
                  <span className="nav-link-line absolute inset-x-4 -bottom-[1px] h-px" aria-hidden="true" />
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label={`Switch to ${theme === "night" ? "day" : "night"} mode`}
            aria-pressed={theme === "night"}
            className="theme-toggle grid h-10 w-10 place-items-center rounded-full transition-all duration-300"
          >
            {theme === "night" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          <Link
            to="/contact"
            data-testid="nav-cta-btn"
            className="button-primary group hidden items-center gap-1.5 rounded-full px-5 py-2.5 font-display text-sm font-semibold text-white transition-transform duration-300 sm:inline-flex"
          >
            Start a Project
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="nav-mobile-menu"
            className="nav-toggle grid h-10 w-10 place-items-center rounded-lg lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
            id="nav-mobile-menu"
            className="nav-mobile-panel overflow-hidden lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {[...navLinks, { to: "/contact", label: "Contact" }].map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`nav-mobile-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `nav-mobile-link block rounded-lg px-4 py-3 font-display text-base font-medium transition-colors duration-300 ${
                      isActive ? "nav-mobile-link-active" : ""
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
